import {useEffect, useState} from 'react';

import {APIError} from 'data/errors';
import {FeaturesEnum, OperationStateType, OperationStateTypesEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {formatApiErrorResponse} from 'presentation/utils/formatApiErrorResponse';

import {HttpStatusCode} from 'data/protocols/http';
import {useHostName} from 'presentation/hooks';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {IDENTIFIER_LENGTH, formatPadStartCpf, getLoginIdentifier} from 'presentation/utils';
import {IFormData} from '../../components/CodeValidationForm/schema';
import {forgotPasswordRecoverUserInfo} from '../../services/forgotPasswordRecoverUserInfo';
import {signUpValidateToken} from '../../services/signUpValidateToken';
import {verifyForgotPasswordToken} from '../../services/verifyForgotPasswordToken';
import {useForgotPasswordData} from '../useForgotPasswordData';
import {useSteps} from '../useSteps';
import {UseVerifyTokenProps} from './types';

import {APIErrorsMessage} from '../../constants/APIErrorsMessage';
import {PublicFlowEnum} from '../../constants/PublicFlowEnum';
import {useSignUpFormDataStore} from '../../sign-up/store/useSignUpFormDataStore';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

const {serverError} = translations['pt-br'].commons;
const {
	expiredCode,
	invalidCode,
	attemptsRemainingSingular,
	attemptsRemainingPlural,
	rateLimitExceededTitle,
	rateLimitExceededDescription,
	rateLimitExceededMessage,
	rateLimitModalWarning,
	retryAgainIn,
} = translations['pt-br'].publicFlow.validateToken;

const INVALID_TOKEN_ERROR_MESSAGES = ['Token Invalido', APIErrorsMessage.INVALID_TOKEN];

export function useVerifyToken({methods, flowType}: UseVerifyTokenProps) {
	const isForgotPassword = flowType === PublicFlowEnum.FORGOT_PASSWORD;
	const {
		getValues,
		formState: {isValidating},
	} = methods;
	const [requestState, setRequestState] = useState<OperationStateType | null>(null);
	const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
	const [apiErrorMsg, setApiErrorMsg] = useState('');
	const [openRateLimitModal, setOpenRateLimitModal] = useState(false);
	const [rateLimitRetryAfter, setRateLimitRetryAfter] = useState<number | null>(null);
	const {handleNextStep} = useSteps(flowType);
	const signUpFormData = useSignUpFormDataStore(state => state.signUpFormData);
	const setSignUpFormData = useSignUpFormDataStore(state => state.setSignUpFormData);

	const {data: forgotPasswordFormData, setForgotPasswordData: setForgotPasswordFormData} =
		useForgotPasswordData();

	const {isEnabled: isPasswordResetRateLimitEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_PASSWORD_RESET_RATE_LIMIT,
	);

	const {
		dataHostNameState: {productCode},
	} = useHostName();

	const {beneficiary} = useAccountDataStore(store => store.accountData);

	const signUpData = signUpFormData;
	const forgotPasswordData = forgotPasswordFormData;

	const formFieldValues = getValues();
	const isDirty = Object.values(formFieldValues).every(val => val !== '');
	const showAPIError = requestState === OperationStateTypesEnum.ERROR && isDirty;

	const handleOpenSnackbar = () => {
		setOpenSnackbar(true);
	};

	const handleCloseSnackbar = () => {
		setOpenSnackbar(false);
	};

	const handleCloseRateLimitModal = () => {
		setOpenRateLimitModal(false);
	};

	const buildInvalidCodeMessage = (remaining: number): string => {
		if (remaining === 1) {
			return `${invalidCode}. ${attemptsRemainingSingular}`;
		}
		return `${invalidCode}. ${remaining} ${attemptsRemainingPlural}`;
	};

	const handleRateLimitError = (error: APIError) => {
		const remaining = error.headers?.['x-ratelimit-remaining'];
		const retryAfter = error.headers?.['retry-after'];

		const remainingCount = remaining !== undefined ? Number(remaining) : 0;

		if (remainingCount === 0) {
			setApiErrorMsg(rateLimitExceededMessage);
			setRateLimitRetryAfter(retryAfter !== undefined ? Number(retryAfter) : 0);
			setOpenRateLimitModal(true);
			return;
		}
		setApiErrorMsg(buildInvalidCodeMessage(remainingCount));
	};

	const handleTokenValidationError = (error: APIError) => {
		const parsedResponse = formatApiErrorResponse(error);
		const isRateLimited = error.statusCode === HttpStatusCode.tooManyRequests;
		const isUnprocessableError = error.statusCode === HttpStatusCode.unprocessable;
		const isInvalidTokenError =
			isUnprocessableError && INVALID_TOKEN_ERROR_MESSAGES.includes(parsedResponse);

		if (isRateLimited || isInvalidTokenError) {
			handleRateLimitError(error);
			return;
		}

		if (isUnprocessableError && parsedResponse === APIErrorsMessage.EXPIRED_TOKEN) {
			setApiErrorMsg(expiredCode);
			return;
		}

		setApiErrorMsg(
			error.statusCode === HttpStatusCode.serverError ? serverError : parsedResponse,
		);
	};

	const handleGetCdPessoaPrivate = async () => {
		try {
			const {dtNascimentoUsuarioC, nuCpfC} = beneficiary;
			const cpf = formatPadStartCpf(nuCpfC);
			const tipoId = cpf.length === IDENTIFIER_LENGTH.cpf ? getLoginIdentifier(cpf) : '1';

			setRequestState(OperationStateTypesEnum.PENDING);
			const {cdPessoa} = await forgotPasswordRecoverUserInfo({
				cdEmpresaPlano: productCode,
				dtNascimento: dtNascimentoUsuarioC,
				id: nuCpfC,
				tipoId,
				phone: '',
			});

			setForgotPasswordFormData(state => ({
				...state,
				cdPessoa,
			}));
		} catch (error) {
			if (error instanceof APIError) {
				setRequestState(OperationStateTypesEnum.ERROR);
				const parsedResponse = formatApiErrorResponse(error);
				setApiErrorMsg(parsedResponse);

				if (error.statusCode === HttpStatusCode.serverError) {
					setApiErrorMsg(serverError);
				}
			}
		}
	};

	const handleVerifyTokenPrivate = async ({
		code1,
		code2,
		code3,
		code4,
		code5,
		code6,
		code7,
	}: IFormData) => {
		try {
			const cdToken = code1 + code2 + code3 + code4 + code5 + code6 + code7;

			setRequestState(OperationStateTypesEnum.PENDING);
			await signUpValidateToken({
				cdEmpresaPlano: productCode,
				cdToken,
				cdPessoa: forgotPasswordData.cdPessoa || '',
			});

			setRequestState(OperationStateTypesEnum.DONE);

			if (isForgotPassword) {
				setForgotPasswordFormData(state => ({
					...state,
					token: cdToken,
				}));
			} else {
				setSignUpFormData(state => ({
					...state,
					token: cdToken,
				}));
			}
			handleOpenSnackbar();
			handleNextStep();
		} catch (error) {
			if (error instanceof APIError) {
				setRequestState(OperationStateTypesEnum.ERROR);
				const parsedResponse = formatApiErrorResponse(error);
				setApiErrorMsg(parsedResponse);
				const isUnprocessableError = error.statusCode === HttpStatusCode.unprocessable;

				if (isUnprocessableError && parsedResponse === APIErrorsMessage.INVALID_TOKEN) {
					setApiErrorMsg(invalidCode);
				}
				if (isUnprocessableError && parsedResponse === APIErrorsMessage.EXPIRED_TOKEN) {
					setApiErrorMsg(expiredCode);
				}
				if (error.statusCode === HttpStatusCode.serverError) {
					setApiErrorMsg(serverError);
				}
			}
		}
	};

	const handleVerifyToken = async ({
		code1,
		code2,
		code3,
		code4,
		code5,
		code6,
		code7,
	}: IFormData) => {
		try {
			const cdToken = code1 + code2 + code3 + code4 + code5 + code6 + code7;
			if (isForgotPassword) {
				setRequestState(OperationStateTypesEnum.PENDING);
				await verifyForgotPasswordToken({
					cdEmpresaPlano: forgotPasswordData.cdEmpresaPlano,
					token: cdToken,
					dtNascimento: forgotPasswordData.dtNascimento,
					id: forgotPasswordData.id,
					tipoId: forgotPasswordData.tipoId,
				});
				setRequestState(OperationStateTypesEnum.DONE);

				setForgotPasswordFormData(state => ({
					...state,
					token: cdToken,
				}));
			} else {
				await signUpValidateToken({
					cdEmpresaPlano: signUpData.cdEmpresaPlano,
					cdPessoa: signUpData.cdPessoa,
					cdToken,
				});
				setSignUpFormData(state => ({
					...state,
					token: cdToken,
				}));
			}
			handleOpenSnackbar();
			handleNextStep();
		} catch (error) {
			if (error instanceof APIError) {
				setRequestState(OperationStateTypesEnum.ERROR);

				if (isForgotPassword && isPasswordResetRateLimitEnabled) {
					handleTokenValidationError(error);
					return;
				}

				const parsedResponse = formatApiErrorResponse(error);
				setApiErrorMsg(parsedResponse);

				if (
					error.statusCode === HttpStatusCode.unprocessable &&
					parsedResponse === APIErrorsMessage.INVALID_TOKEN
				) {
					setApiErrorMsg(invalidCode);
				}

				if (
					error.statusCode === HttpStatusCode.unprocessable &&
					parsedResponse === APIErrorsMessage.EXPIRED_TOKEN
				) {
					setApiErrorMsg(expiredCode);
				}

				if (error.statusCode === HttpStatusCode.serverError) {
					setApiErrorMsg(serverError);
				}
			}
		}
	};

	const clearAPIError = () => {
		setRequestState(null);
	};

	const resetRateLimitState = () => {
		setRateLimitRetryAfter(null);
		setOpenRateLimitModal(false);
		setApiErrorMsg('');
	};

	useEffect(() => {
		if (isValidating) {
			clearAPIError();
		}
	}, [isValidating]);

	return {
		openSnackbar,
		requestState,
		apiErrorMsg,
		showAPIError,
		handleCloseSnackbar,
		clearAPIError,
		handleVerifyToken,
		handleVerifyTokenPrivate,
		handleGetCdPessoaPrivate,
		openRateLimitModal,
		rateLimitRetryAfter,
		rateLimitModalWarning,
		rateLimitExceededTitle,
		rateLimitExceededDescription,
		retryAgainIn,
		handleCloseRateLimitModal,
		resetRateLimitState,
	};
}
